import { computed, ComputedRef } from "vue";
import { storeToRefs } from "pinia";

import * as tmi from "@/constants";

import { useAuthStore } from "@/stores/Auth";
import { usePermissionsStore } from "@/stores/Permissions";
import { useProductsStore } from "@/stores/Products";

// UTILITIES
// import { consoleLoggerFactory, getDefaultLoggerOptions, ILogger, LogLevels } from "@/types/console-logger";
// const logger = createDefaultLogger();

export function useMenuPermissions() {
    const { isAuthenticated } = storeToRefs( useAuthStore() );
    const permissionsStore = usePermissionsStore();
    const {
        hasArticleAccess,
        hasBonusReportAccess,
        hasCourseAccess,
        hasTradeOfTheDayAccess,
        hasEtfListingAccess,
        hasEquityCalculatorAccess,
        hasMainSearchAccess,
        hasMajorIndexBreadthAccess,
        hasMyLibraryAccess,
        hasNyseBpiAccess,
        hasOptionsCalculatorAccess,
        hasPortfolioBuilderAccess,
        hasPositionSizeCalculatorAccess,
        hasStockReplacementStrategyCalculatorAccess,
        hasProductTradeAlertsAccess,
        hasProfileAccess,
        hasRelativeStrengthMatrixAccess,
        hasSectorAccess,
        hasSectorAlertsAccess,
        hasSectorBpiAccess,
        hasSpreadCalculatorAccess,
        hasToolsAccess,
        hasWebinarAccess,
        isAllianceMember
    } = storeToRefs( permissionsStore );
    const productsStore = useProductsStore();

    function hasAccessBasedOnShortCode( shortCode: string | undefined ): boolean {
        let hasAccess: boolean = false;
        if ( shortCode ) {
            hasAccess = permissionsStore.hasAccessBasedOnShortCode( shortCode );
        }

        return hasAccess;
    }

    // Main Search Permissions
    const showMainSearch = computed< boolean >( () => {
        return hasMainSearchAccess.value;
    } );

    // Products Menu Permissions
    const showProductsMenuItem = computed< boolean >( (): boolean => productsStore.allProducts.length > 0 );

    // Shadow Spike Scanner requires the SSS grant plus the feature permission.
    // SSS is an exclusive tier server-side (never granted via the
    // alliance/fullaccess expansion), so holding the grant is proof of a
    // direct assignment. Mirrors the /shadow-spike route guard.
    const showShadowSpikeScannerMenuOption = computed< boolean >( () =>
        isAuthenticated.value &&
        hasAccessBasedOnShortCode( tmi.ShadowScannerShortCode ) &&
        permissionsStore.hasFeatureAccess( tmi.ShadowScannerPermissionKey ) );

    // Alerts Menu Permissions
    const showAlertsMenuItem = computed< boolean >( () => isAuthenticated.value && ( showSectorAlertsMenuOption.value || showTradeAlertsMenuOption.value ) );
    const showSectorAlertsMenuOption = computed< boolean >( () => hasSectorAlertsAccess.value && hasSectorAccess.value );
    const showTradeAlertsMenuOption = computed< boolean >( () => hasProductTradeAlertsAccess.value );
    const showTradeOfTheDayMenuOption = computed< boolean >( () => true );
    // const showTradeOfTheDayMenuOption = computed< boolean >( () => hasTradeOfTheDayAccess.value );

    // Tools Menu Permissions
    const showToolsMenuItem = computed< boolean >( () => isAuthenticated.value && hasToolsAccess.value );

    // -- Indicators
    const showIndicatorsSection = computed< boolean >( () => showSectorBpiMenuOption.value || showNyseBpiMenuOption.value || showRelativeStrengthMatrixMenuOption.value || showMajorIndexBreadthMenuOption.value );
    const showSectorBpiMenuOption = computed< boolean >( () => hasSectorBpiAccess.value );
    const showNyseBpiMenuOption = computed< boolean >( () => hasNyseBpiAccess.value );
    const showRelativeStrengthMatrixMenuOption = computed< boolean >( () => hasRelativeStrengthMatrixAccess.value );
    const showMajorIndexBreadthMenuOption = computed< boolean >( () => hasMajorIndexBreadthAccess.value );

    // -- Reference Tools
    const showReferenceToolsSection = computed< boolean >( () => showEtfListingMenuOption.value || showSectorTreeMenuOption.value || showSectorsMenuOption.value );
    const showEtfListingMenuOption = computed< boolean >( () => hasEtfListingAccess.value );
    const showSectorTreeMenuOption = computed< boolean >( () => hasSectorAccess.value );
    const showSectorsMenuOption = computed< boolean >( () => hasSectorAccess.value );

    // -- Portfolio Tools
    const showPortfolioToolsSection = computed< boolean >( () => showPortfolioBuilderMenuOption.value || showEquityCalculatorMenuOption.value || showOptionsCalculatorMenuOption.value || showPositionSizeCalculatorMenuOption.value || showStockReplacementStrategyCalculatorMenuOption.value || showSpreadCalculatorMenuOption.value );
    const showPortfolioBuilderMenuOption = computed< boolean >( () => hasPortfolioBuilderAccess.value );
    const showOptionsCalculatorMenuOption = computed< boolean >( () => hasOptionsCalculatorAccess.value );
    const showPositionSizeCalculatorMenuOption = computed< boolean >( () => hasPositionSizeCalculatorAccess.value );
    const showStockReplacementStrategyCalculatorMenuOption = computed< boolean >( () => hasStockReplacementStrategyCalculatorAccess.value );
    const showSpreadCalculatorMenuOption = computed< boolean >( () => hasSpreadCalculatorAccess.value );

    // Learning Menu Permissions
    const showLearningMenuItem = computed< boolean >( () => showArticlesOption.value || showBonusReportOption.value || showCoursesMenuOption.value || showWebinarsMenuOption.value );
    const showArticlesOption = computed< boolean >( () => hasArticleAccess.value );
    const showBonusReportOption = computed< boolean >( () => hasBonusReportAccess.value );
    const showCoursesMenuOption = computed< boolean >( () => hasCourseAccess.value );
    const showWebinarsMenuOption = computed< boolean >( () => hasWebinarAccess.value );

    // User Profile Permissions
    const showMyLibrary = computed< boolean >( () => hasMyLibraryAccess.value );
    const showProfileDropdown = computed< boolean >( () => hasProfileAccess.value );

    // TEMPORARILY DISABLED - it no worky...
    const showEquityCalculatorMenuOption: ComputedRef< boolean > = computed( () => hasEquityCalculatorAccess.value );

    return {
        hasAccessBasedOnShortCode,

        isAllianceMember,
        showMyLibrary,
        showProfileDropdown,
        showBonusReportOption,
        showCoursesMenuOption,
        showEtfListingMenuOption,
        showEquityCalculatorMenuOption,
        showLearningMenuItem,
        showMainSearch,
        showMajorIndexBreadthMenuOption,
        showNyseBpiMenuOption,
        showOptionsCalculatorMenuOption,
        showPortfolioBuilderMenuOption,
        showPositionSizeCalculatorMenuOption,
        showStockReplacementStrategyCalculatorMenuOption,
        showRelativeStrengthMatrixMenuOption,
        showSectorAlertsMenuOption,
        showSectorBpiMenuOption,
        showSectorsMenuOption,
        showSectorTreeMenuOption,
        showShadowSpikeScannerMenuOption,
        showSpreadCalculatorMenuOption,
        showToolsMenuItem,
        showTradeAlertsMenuOption,
        showTradeOfTheDayMenuOption,
        showWebinarsMenuOption,

        showProductsMenuItem,
        showAlertsMenuItem,
        showIndicatorsSection,
        showPortfolioToolsSection,
        showReferenceToolsSection,
        showArticlesOption
    };
}
